import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface MeResponse {
  contact: PortalContact | null;
  remainingBackupCodes?: number;
}

export function PortalProfilePage(): React.ReactElement {
  const navigate = useNavigate();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [remainingCodes, setRemainingCodes] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<MeResponse>('/api/portal/auth/me');
      if (!r.contact) {
        navigate('/login', { replace: true, state: { from: '/portal/profile' } });
        return;
      }
      setContact(r.contact);
      setName(r.contact.name ?? '');
      setRemainingCodes(r.remainingBackupCodes ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    setSavingName(true);
    setError(null);
    setInfo(null);
    try {
      await apiFetch('/api/portal/auth/me/preferences', {
        method: 'PATCH',
        json: { name: name.trim() },
      });
      setInfo('Name updated.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setSavingName(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPw !== confirmPw) {
      setError('New passwords do not match.');
      return;
    }
    setSavingPw(true);
    try {
      await apiFetch('/api/portal/auth/change-password', {
        method: 'POST',
        json: { currentPassword: currentPw, newPassword: newPw },
      });
      setInfo('Password changed.');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Change failed.');
    } finally {
      setSavingPw(false);
    }
  }

  if (loading)
    return (
      <div className="bg-white">
        <PortalHealthBanner />
        <PortalNavBar contact={null} />
        <main className="mx-auto max-w-2xl px-6 py-10 sm:px-8">
          <p className="text-[var(--color-muted)]">Loading…</p>
        </main>
      </div>
    );
  if (!contact) return <></>;

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} />
      <main className="mx-auto max-w-2xl space-y-6 px-6 py-10 sm:px-8">
      <div>
        <Link to="/portal" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Help center
        </Link>
        <h1 className="mt-1 font-display text-2xl font-bold text-[var(--color-header)]">My profile</h1>
      </div>

      {info && (
        <Card className="border-green-300 bg-green-50/40">
          <p className="text-sm text-green-900">{info}</p>
        </Card>
      )}
      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Account</h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Email</dt>
            <dd>{contact.email}</dd>
          </div>
          {remainingCodes !== null && (
            <div>
              <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Backup codes left</dt>
              <dd>{remainingCodes}</dd>
            </div>
          )}
        </dl>
        <form onSubmit={saveName} className="mt-4 space-y-3">
          <FormField
            label="Display name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" disabled={savingName}>
            {savingName ? 'Saving…' : 'Save name'}
          </Button>
        </form>
      </Card>

      {contact.isOrgAdmin && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Integrations</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Pull your data on demand with API keys, or receive signed pushes whenever a time entry
            changes via webhooks. Webhooks are only available once your T3 contact enables them
            for your organization.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/portal/api-keys">
              <Button variant="outline">Manage API keys</Button>
            </Link>
            <Link to="/portal/webhooks">
              <Button variant="outline">Manage webhooks</Button>
            </Link>
          </div>
        </Card>
      )}

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Change password</h2>
        <form onSubmit={changePassword} className="mt-3 space-y-3">
          <FormField
            label="Current password"
            type="password"
            name="current"
            autoComplete="current-password"
            required
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
          />
          <FormField
            label="New password"
            type="password"
            name="new"
            autoComplete="new-password"
            required
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            hint="At least 12 characters with upper, lower, and a number."
          />
          <FormField
            label="Confirm new password"
            type="password"
            name="confirm"
            autoComplete="new-password"
            required
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />
          <Button type="submit" disabled={savingPw}>
            {savingPw ? 'Updating…' : 'Change password'}
          </Button>
        </form>
      </Card>
      </main>
      <PortalFooter />
    </div>
  );
}
